// src/components/DashboardHeader/MemberCard.jsx
import React, { useMemo, useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Box,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import { GiScrollQuill } from "react-icons/gi";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { getMemberPhotoUrl } from "../../utils/utils";
import { useUser } from "../../context/UserContext";
import ContactInfoModal from "./ContactInfoModal";
import { theme } from "../../theme/colors";

/** Normalize party input: "D", "Democrat", "democratic party", etc. */
function normalizeParty(party) {
  if (!party) return undefined;
  const p = String(party).trim().toLowerCase();
  if (p === "d" || p.startsWith("dem")) return "D";
  if (p === "r" || p.startsWith("rep")) return "R";
  if (p === "i" || p.startsWith("ind")) return "I";
  return undefined;
}

/** Ensure HTTPS and support protocol-relative //host paths */
function safePhotoUrl(url) {
  if (!url) return undefined;
  try {
    if (url.startsWith("//")) return `https:${url}`;
    if (url.startsWith("http://")) return url.replace("http://", "https://");
    return url;
  } catch {
    return undefined;
  }
}

/** Fallback initials for avatar if no image */
function initialsFromName(name) {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase();
}

// Brand from theme

// Party indicator bubble on avatar
const PartyBubble = ({ party }) => {
  const color = useMemo(() => {
    const p = normalizeParty(party);
    if (p === "D") return "#2563eb";
    if (p === "R") return "#dc2626";
    if (p === "I") return "#6b7280";
    return undefined;
  }, [party]);

  if (!color) return null;
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: -2,
        right: -2,
        width: 16,
        height: 16,
        borderRadius: "50%",
        bgcolor: color,
        border: "2px solid white",
      }}
    />
  );
};

const MemberCard = React.memo(function MemberCard({
  bioguideId,             // resolves photo internally
  role,                   // "Representative" | "Senator"
  regionLabel,            // "WI-04" or "WI"
  party,                  // "D" | "R" | variants
  onViewLegislation,
  onMessage,              // optional
  name,                   // optional override; context used if available
}) {
  const [resolvedPhoto, setResolvedPhoto] = useState(undefined);
  const [showContactModal, setShowContactModal] = useState(false);

  // Pull names from context (single source of truth)
  const {
    userRepsName,
    userSenOneName,
    userSenTwoName,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
  } = useUser();

  // Choose a display name: prefer context match by ID; then role fallback; then prop
  const displayName = useMemo(() => {
    if (bioguideId) {
      if (bioguideId === userRepsBioguideID && userRepsName) return userRepsName;
      if (bioguideId === userSenOneBioguideID && userSenOneName) return userSenOneName;
      if (bioguideId === userSenTwoBioguideID && userSenTwoName) return userSenTwoName;
    }
    if (/(representative|delegate)/i.test(role || "") && userRepsName) return userRepsName;
    if (/senator/i.test(role || "")) return userSenOneName || userSenTwoName || name || "Senator";
    return name || role || "Member";
  }, [
    bioguideId,
    role,
    name,
    userRepsName,
    userSenOneName,
    userSenTwoName,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
  ]);

  // Resolve photo once we have a bioguideId
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!bioguideId) {
          if (mounted) setResolvedPhoto(undefined);
          return;
        }
        const url = await getMemberPhotoUrl(bioguideId);
        if (mounted) setResolvedPhoto(safePhotoUrl(url));
      } catch {
        if (mounted) setResolvedPhoto(undefined);
      }
    })();
    return () => { mounted = false; };
  }, [bioguideId]);

  const cleanParty = normalizeParty(party);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        width: "100%",
        maxWidth: 420,
        mx: "auto",
        boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
      }}
    >
      <CardHeader
        titleTypographyProps={{ fontWeight: 700, fontSize: "1.1rem" }}
        subheaderTypographyProps={{ fontSize: "0.9rem", color: "text.secondary" }}
        title={displayName}
        // subheader={regionLabel || ""}
        sx={{ pb: 0 }}
        avatar={
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={resolvedPhoto}
              alt={displayName}
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#e0e0e0",
                border: `2px solid ${theme.primary}`,
                fontWeight: 800,
              }}
              imgProps={{
                loading: "lazy",
                referrerPolicy: "no-referrer",
                decoding: "async",
              }}
            >
              {!resolvedPhoto ? initialsFromName(displayName) : null}
            </Avatar>
            <PartyBubble party={cleanParty} />
          </Box>
        }
      />

      <CardContent sx={{ pt: 2 }}>
        <Stack direction="row" spacing={1.2} sx={{ mb: 2 }}>
          <Chip
            size="small"
            label={role || "Member"}
            sx={{
              bgcolor: "#0f1554",
              color: "#fff",
              fontWeight: 700,
              borderRadius: "6px",
            }}
          />
        </Stack>

        <Stack spacing={1.5}>
          <Button
            fullWidth
            onClick={() => setShowContactModal(true)}
            startIcon={<EmailOutlinedIcon />}
            sx={{
              height: 48,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              background: theme.gradient,
              color: "#fff",
              "&:hover": { opacity: 0.95, background: theme.gradient },
            }}
            aria-label="Contact"
          >
            Contact
          </Button>
          <Button
            fullWidth
            onClick={onViewLegislation}
            startIcon={<GiScrollQuill />}
            sx={{
              height: 48,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              background: theme.gradient,
              color: "#fff",
              "&:hover": { opacity: 0.95, background: theme.gradient },
            }}
            aria-label="View supported legislation"
          >
            View Supported Legislation
          </Button>
        </Stack>
      </CardContent>

      <ContactInfoModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        bioguideId={bioguideId}
        memberName={displayName}
        memberRole={role}
      />
    </Card>
  );
});

export default MemberCard;
